import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const WarrantyDataGrid = ({ onAssetSelect, selectedAssets, onBulkAction }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'daysRemaining', direction: 'asc' });
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('all');

  const warrantyData = [
    {
      id: 'AT-2025-0001',
      assetType: 'Dell Laptop',
      model: 'Latitude 7420',
      purchaseDate: '2022-03-15',
      warrantyEndDate: '2025-08-25',
      daysRemaining: 6,
      renewalStatus: 'Pending',
      vendor: 'Dell Technologies',
      cost: '₹1,299.00',
      owner: 'Priya Patel',
      department: 'Marketing',
      priority: 'critical'
    },
    {
      id: 'AT-2025-0002',
      assetType: 'HP Printer',
      model: 'LaserJet Pro 4301dw',
      purchaseDate: '2022-05-20',
      warrantyEndDate: '2025-09-15',
      daysRemaining: 27,
      renewalStatus: 'Not Started',
      vendor: 'HP Inc.',
      cost: '₹449.00',
      owner: 'Rahul Verma',
      department: 'Operations',
      priority: 'critical'
    },
    {
      id: 'AT-2025-0003',
      assetType: 'Lenovo Desktop',
      model: 'ThinkCentre M75q',
      purchaseDate: '2022-07-10',
      warrantyEndDate: '2025-10-08',
      daysRemaining: 50,
      renewalStatus: 'In Progress',
      vendor: 'Lenovo',
      cost: '₹899.00',
      owner: 'Anjali Singh',
      department: 'Finance',
      priority: 'warning'
    },
    {
      id: 'AT-2025-0004',
      assetType: 'Apple MacBook',
      model: 'MacBook Pro 14"',
      purchaseDate: '2022-09-05',
      warrantyEndDate: '2025-11-20',
      daysRemaining: 93,
      renewalStatus: 'Completed',
      vendor: 'Apple Inc.',
      cost: '₹2,499.00',
      owner: 'Vikram Nair',
      department: 'Design',
      priority: 'normal'
    },
    {
      id: 'AT-2025-0005',
      assetType: 'Dell Server',
      model: 'PowerEdge R750',
      purchaseDate: '2021-12-01',
      warrantyEndDate: '2025-08-30',
      daysRemaining: 11,
      renewalStatus: 'Urgent',
      vendor: 'Dell Technologies',
      cost: '₹4,299.00',
      owner: 'Neha Gupta',
      department: 'Sales',
      priority: 'critical'
    },
    {
      id: 'AT-2025-0006',
      assetType: 'Canon Printer',
      model: 'imageRUNNER C3226i',
      purchaseDate: '2022-04-18',
      warrantyEndDate: '2025-12-15',
      daysRemaining: 118,
      renewalStatus: 'Scheduled',
      vendor: 'Canon USA',
      cost: '₹1,899.00',
      owner: 'Reception',
      department: 'Administration',
      priority: 'normal'
    },
    {
      id: 'AT-2025-0007',
      assetType: 'Microsoft Surface',
      model: 'Surface Pro 9',
      purchaseDate: '2022-11-22',
      warrantyEndDate: '2025-09-30',
      daysRemaining: 42,
      renewalStatus: 'Quote Requested',
      vendor: 'Microsoft',
      cost: '₹1,599.00',
      owner: 'Lisa Wang',
      department: 'Sales',
      priority: 'warning'
    },
    {
      id: 'AT-2025-0008',
      assetType: 'Cisco Switch',
      model: 'Catalyst 2960-X',
      purchaseDate: '2021-08-15',
      warrantyEndDate: '2025-08-28',
      daysRemaining: 9,
      renewalStatus: 'Renewal Approved',
      vendor: 'Cisco Systems',
      cost: '₹2,199.00',
      owner: 'Network Team',
      department: 'IT',
      priority: 'critical'
    }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'urgent', label: 'Urgent' }
  ];

  const vendorOptions = [
    { value: 'all', label: 'All Vendors' },
    { value: 'dell', label: 'Dell Technologies' },
    { value: 'hp', label: 'HP Inc.' },
    { value: 'lenovo', label: 'Lenovo' },
    { value: 'apple', label: 'Apple Inc.' },
    { value: 'canon', label: 'Canon USA' },
    { value: 'microsoft', label: 'Microsoft' },
    { value: 'cisco', label: 'Cisco Systems' }
  ];

  const filteredAndSortedData = useMemo(() => {
    let filtered = warrantyData?.filter(item => {
      const matchesSearch = searchQuery === '' || 
        item?.id?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        item?.assetType?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        item?.owner?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        item?.vendor?.toLowerCase()?.includes(searchQuery?.toLowerCase());

      const matchesStatus = statusFilter === 'all' || item?.renewalStatus?.toLowerCase()?.replace(' ', '-') === statusFilter;

      const matchesVendor = vendorFilter === 'all' || 
        item?.vendor?.toLowerCase()?.includes(vendorFilter);

      return matchesSearch && matchesStatus && matchesVendor;
    });

    if (sortConfig?.key) {
      filtered?.sort((a, b) => {
        let aValue = a?.[sortConfig?.key];
        let bValue = b?.[sortConfig?.key];

        if (sortConfig?.key === 'daysRemaining') {
          aValue = parseInt(aValue);
          bValue = parseInt(bValue);
        }

        if (aValue < bValue) {
          return sortConfig?.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig?.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [warrantyData, searchQuery, statusFilter, vendorFilter, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig?.key === key && prevConfig?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical':
        return 'text-error bg-error/10';
      case 'warning':
        return 'text-warning bg-warning/10';
      default:
        return 'text-success bg-success/10';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'urgent':
        return 'text-error bg-error/10';
      case 'pending': case'not started':
        return 'text-warning bg-warning/10';
      case 'in progress': case'quote requested':
        return 'text-accent bg-accent/10';
      case 'completed': case'renewal approved':
        return 'text-success bg-success/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const isAllSelected = selectedAssets?.length === filteredAndSortedData?.length && filteredAndSortedData?.length > 0;
  const isIndeterminate = selectedAssets?.length > 0 && selectedAssets?.length < filteredAndSortedData?.length;

  const handleSelectAll = (checked) => {
    if (checked) {
      onAssetSelect(filteredAndSortedData?.map(item => item?.id));
    } else {
      onAssetSelect([]);
    }
  };

  const handleSelectAsset = (assetId, checked) => {
    if (checked) {
      onAssetSelect([...selectedAssets, assetId]);
    } else {
      onAssetSelect(selectedAssets?.filter(id => id !== assetId));
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Header with Filters */}
      <div className="p-6 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div>
            <h3 className="text-lg font-semibold text-card-foreground">Warranty Expiration Tracking</h3>
            <p className="text-sm text-muted-foreground">Assets requiring warranty attention</p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <Input
              type="search"
              placeholder="Search assets, owners, or vendors..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="sm:w-64"
            />
            
            <Select
              options={statusOptions}
              value={statusFilter}
              onChange={setStatusFilter}
              placeholder="Filter by status"
              className="sm:w-48"
            />
            
            <Select
              options={vendorOptions}
              value={vendorFilter}
              onChange={setVendorFilter}
              placeholder="Filter by vendor"
              className="sm:w-48"
            />
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedAssets?.length > 0 && (
          <div className="mt-4 p-4 bg-accent/5 border border-accent/20 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-accent">
                {selectedAssets?.length} asset{selectedAssets?.length > 1 ? 's' : ''} selected
              </span>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onBulkAction('renew')}
                  iconName="RefreshCw"
                  iconPosition="left"
                >
                  Bulk Renew
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onBulkAction('quote')}
                  iconName="FileText"
                  iconPosition="left"
                >
                  Request Quotes
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onBulkAction('export')}
                  iconName="Download"
                  iconPosition="left"
                >
                  Export
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Data Grid */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="w-12 p-4">
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isIndeterminate}
                  onChange={(e) => handleSelectAll(e?.target?.checked)}
                />
              </th>
              <th className="text-left p-4 font-medium text-muted-foreground">
                <button
                  onClick={() => handleSort('id')}
                  className="flex items-center space-x-1 hover:text-foreground transition-smooth"
                >
                  <span>Asset ID</span>
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="text-left p-4 font-medium text-muted-foreground">
                <button
                  onClick={() => handleSort('assetType')}
                  className="flex items-center space-x-1 hover:text-foreground transition-smooth"
                >
                  <span>Asset Type</span>
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="text-left p-4 font-medium text-muted-foreground">
                <button
                  onClick={() => handleSort('purchaseDate')}
                  className="flex items-center space-x-1 hover:text-foreground transition-smooth"
                >
                  <span>Purchase Date</span>
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="text-left p-4 font-medium text-muted-foreground">
                <button
                  onClick={() => handleSort('warrantyEndDate')}
                  className="flex items-center space-x-1 hover:text-foreground transition-smooth"
                >
                  <span>Warranty End</span>
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="text-left p-4 font-medium text-muted-foreground">
                <button
                  onClick={() => handleSort('daysRemaining')}
                  className="flex items-center space-x-1 hover:text-foreground transition-smooth"
                >
                  <span>Days Remaining</span>
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="text-left p-4 font-medium text-muted-foreground">
                <button
                  onClick={() => handleSort('renewalStatus')}
                  className="flex items-center space-x-1 hover:text-foreground transition-smooth"
                >
                  <span>Renewal Status</span>
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="text-left p-4 font-medium text-muted-foreground">
                <button
                  onClick={() => handleSort('vendor')}
                  className="flex items-center space-x-1 hover:text-foreground transition-smooth"
                >
                  <span>Vendor</span>
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="text-left p-4 font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filteredAndSortedData?.map((asset) => (
              <tr key={asset?.id} className="hover:bg-muted/30 transition-smooth">
                <td className="p-4">
                  <Checkbox
                    checked={selectedAssets?.includes(asset?.id)}
                    onChange={(e) => handleSelectAsset(asset?.id, e?.target?.checked)}
                  />
                </td>
                <td className="p-4">
                  <div className="font-medium text-card-foreground">{asset?.id}</div>
                  <div className="text-sm text-muted-foreground">{asset?.owner}</div>
                </td>
                <td className="p-4">
                  <div className="font-medium text-card-foreground">{asset?.assetType}</div>
                  <div className="text-sm text-muted-foreground">{asset?.model}</div>
                </td>
                <td className="p-4 text-card-foreground">{formatDate(asset?.purchaseDate)}</td>
                <td className="p-4 text-card-foreground">{formatDate(asset?.warrantyEndDate)}</td>
                <td className="p-4">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(asset?.priority)}`}>
                    {asset?.daysRemaining} days
                  </span>
                </td>
                <td className="p-4">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(asset?.renewalStatus)}`}>
                    {asset?.renewalStatus}
                  </span>
                </td>
                <td className="p-4">
                  <div className="font-medium text-card-foreground">{asset?.vendor}</div>
                  <div className="text-sm text-muted-foreground">{asset?.cost}</div>
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="RefreshCw"
                      iconPosition="left"
                    >
                      Renew
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      iconName="MoreHorizontal"
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Footer */}
      <div className="p-4 border-t border-border bg-muted/20">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>Showing {filteredAndSortedData?.length} of {warrantyData?.length} assets</span>
          <div className="flex items-center space-x-4">
            <span>Critical: {filteredAndSortedData?.filter(a => a?.priority === 'critical')?.length}</span>
            <span>Warning: {filteredAndSortedData?.filter(a => a?.priority === 'warning')?.length}</span>
            <span>Normal: {filteredAndSortedData?.filter(a => a?.priority === 'normal')?.length}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WarrantyDataGrid;